/** Tiny typed DOM builders. No framework, no innerHTML for untrusted values. */

export type Child = Node | string | null | undefined | false;

type EventMap = HTMLElementEventMap;

export interface ElementOptions {
  readonly className?: string;
  readonly text?: string;
  readonly attrs?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly dataset?: Readonly<Record<string, string>>;
  readonly on?: {
    readonly [K in keyof EventMap]?: (event: EventMap[K]) => void;
  };
  readonly children?: readonly Child[];
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElementOptions = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (options.className !== undefined) {
    node.className = options.className;
  }
  if (options.text !== undefined) {
    node.textContent = options.text;
  }
  if (options.attrs !== undefined) {
    for (const [name, value] of Object.entries(options.attrs)) {
      if (value === undefined || value === false) {
        continue;
      }
      node.setAttribute(name, value === true ? "" : String(value));
    }
  }
  if (options.dataset !== undefined) {
    for (const [name, value] of Object.entries(options.dataset)) {
      node.dataset[name] = value;
    }
  }
  if (options.on !== undefined) {
    for (const [name, handler] of Object.entries(options.on)) {
      node.addEventListener(name, handler as EventListener);
    }
  }
  if (options.children !== undefined) {
    append(node, ...options.children);
  }

  return node;
}

export function append(parent: Node, ...children: readonly Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) {
      continue;
    }
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

export function clear(node: Node): void {
  while (node.firstChild !== null) {
    node.removeChild(node.firstChild);
  }
}

export function replaceChildren(parent: Node, ...children: readonly Child[]): void {
  clear(parent);
  append(parent, ...children);
}

export function fragment(...children: readonly Child[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  append(frag, ...children);
  return frag;
}
